//#region imports
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

// bootstrap
import { Card, Form, Spinner, Alert, Button } from 'react-bootstrap';

// stripe
import { CardElement, useStripe, useElements, ExpressCheckoutElement } from '@stripe/react-stripe-js';

// redux
import { useSelector, useDispatch } from 'react-redux';
import { selectStripe, selectPaymentIntent, selectGetPaymentIntentStatus, selectGetPaymentIntentError, getPaymentIntent, getPaymentIntentExpressPay, selectGetPaymentExpressIntentStatus, selectExpressIntent, selectGetPaymentExpressIntentError, resetExpressIntent } from 'redux/slices/marketplace/stripe';
import { saveTransaction, selectUserTransactionStatus, selectUserSavedTransaction, selectUserTransactionError } from 'redux/slices/marketplace/user-transaction';
import { selectTableNumber, generatePin, setPin, selectPin, selectShoppingCart, selectShoppingCartPrice, setUserEmail, setUserName, selectUserName, selectUserEmail, selectRetailerId, selectTotalFee } from 'redux/slices/marketplace/user-marketplace';

// custom components
import { CustomSpinner } from '_atoms';

// styles
import { Colors, Spacing } from '_styles';
import { selectUser } from '_redux/slices/website/confirm-booking';
import { setError } from '_redux/slices/beacons/beacons';

// icons 
import ApplePayLogo from '_assets/icons/apple-pay.png';
import GooglePayLogo from '_assets/icons/google-pay.png';
import ApplePayLogoGrey from '_assets/icons/apple-pay-grey.png';
import GooglePayLogoGrey from '_assets/icons/google-pay-grey.png';
//#endregion

const CheckoutForm = ({ onClose }) => {

    // nav
    const history = useHistory();

    //#region stripe
    const stripe = useStripe();
    const elements = useElements();

    //#endregion

    //#region local states
    const [submitted, setSubmitted] = useState(false);                          // indicates if the form has been submitted
    const [savingTransaction, setSavingTransaction] = useState(false);          // indicates if the transaction is being saved
    const [loading, setLoading] = useState(false);                              // indicates if the form is being submitted
    const [error, setError] = useState(null);                                   // error message for payment or transaction saving
    const [disabled, setDisabled] = useState(false);                            // disable pay button if email is invalid

    const [paymentOption, setPaymentOption] = useState('');                 // payment option selected by user (card or express pay)
    const [expressPayReady, setExpressPayReady] = useState(false);
    //#endregion

    //#region redux
    const dispatch = useDispatch();

    const stripeState = useSelector(selectStripe);

    // payment intent handlers
    const paymentIntent = useSelector(selectPaymentIntent);
    const getPaymentIntentStatus = useSelector(selectGetPaymentIntentStatus);
    const getPaymentIntentError = useSelector(selectGetPaymentIntentError);

    // express pay intent handlers
    const expressIntent = useSelector(selectExpressIntent);
    const getPaymentExpressIntentError = useSelector(selectGetPaymentExpressIntentError);
    const getPaymentExpressIntentStatus = useSelector(selectGetPaymentExpressIntentStatus);

    // transaction handlers
    const savedTransaction = useSelector(selectUserSavedTransaction);
    const userTransactionStatus = useSelector(selectUserTransactionStatus);
    const userTransactionError = useSelector(selectUserTransactionError);

    const tableNumber = useSelector(selectTableNumber);
    const pin = useSelector(selectPin);
    const retailerId = useSelector(selectRetailerId);

    // shopping cart
    const shoppingCart = useSelector(selectShoppingCart);
    const amount = useSelector(selectTotalFee);

    // user info
    const userName = useSelector(selectUserName);
    const userEmail = useSelector(selectUserEmail);

    //#endregion

    //#region use effects

    useEffect(() => {
        setError(null);     // reset error state when component mounts
    }, [])

    // set pin on render
    useEffect(() => {
        const temp = generatePin()
        dispatch(setPin(temp))
    }, [])

    // on intialization get express pay intent to show express pay option
    useEffect(() => {
        // handleExpressPay();
        dispatch(resetExpressIntent());
    }, []);

    // get card payment intent status and handle payment if successful
    useEffect(() => {
        // check for payment intent, submitted state, and successful payment intent status
        if (paymentIntent && submitted && getPaymentIntentStatus === 'succeeded') {
            // payment handle successfully, use the payment intent to confirm the payment
            handleStripeCardPayment(paymentIntent)
        }
    }, [getPaymentIntentStatus]);

    // save transaction status and handle post payment actions
    useEffect(() => {
        if (savingTransaction && userTransactionStatus == 'loading') {
            // transaction is being saved, set loading state
            setLoading(true);
        }
        if (savingTransaction && userTransactionStatus === 'succeeded') {
            // transaction saved successfully, reset local state
            setLoading(false);
            setSavingTransaction(false);
            setSubmitted(false);

            // handle post payment actions (e.g., show success message, redirect, etc.)
            console.log('transaction saved successfully...', savedTransaction);
            history.push('/order-complete')

        }
        else if (savingTransaction && userTransactionStatus == 'failed') {
            // error saving transaction, show error message
            setLoading(false);
            setSavingTransaction(false);
            setSubmitted(false);
            // set error message
            setError('Failed to complete purchase. Please try again later.');
        }
    }, [userTransactionStatus])

    // disable pay button if invalid name or email
    useEffect(() => {

        // check valid name
        if (userName == '' || userName == null) {
            setDisabled(true);
        }
        else if (userEmail == '' || userEmail == null) {
            setDisabled(true);
        }
        else if (/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,}$/.test(userEmail)) {
            setDisabled(false);
        }
        else {
            setDisabled(true);
        }
    }, [userEmail, userName])

    //#endregion

    //#region functions

    // create payment intent for express pay
    const handleExpressPay = async () => {
        // reset states
        //setSubmitted(false);
        //setLoading(true);

        // check stripe elements are loaded
        if (!stripe || !elements) {
            console.error('Stripe.js has not loaded correctly.');
            setLoading(false);
            return;
        }

        // create payment method object
        setSubmitted(true)
        dispatch(getPaymentIntentExpressPay({
            amount,
            currency: 'eur',
        }));

    }

    const handleExpressConfirm = async (event) => {
        console.log('Express Checkout confirmed:', event);

        try {

            setLoading(true);

            const result = await stripe.confirmPayment({
                elements,
                clientSecret: expressIntent.clientSecret,
                confirmParams: {
                    return_url: window.location.href,
                },
                redirect: 'if_required',
            });

            if (result.error) {
                console.error('Express Checkout Error:', result.error.message);
                setLoading(false);
                setError(result.error.message);
            } else if (result.paymentIntent?.status === 'succeeded') {
                // keep loading state until transaction is saved
                handleSaveTransaction(expressIntent);
            } else {
                setLoading(false)
                console.warn('Unhandled payment intent status:', result.paymentIntent?.status);
                setError('Payment not completed.');
            }
        } catch (err) {
            setLoading(false);
            console.error('Error confirming Express Checkout:', err);
            setError('Failed to complete Express Checkout.');
        }
    };

    // handle form submission and create payment object to get payment intent from sever for card payment
    const handleSubmitCardPayment = async (event) => {
        setSubmitted(false)
        event.preventDefault();

        setLoading(true);

        if (!stripe || !elements) {
            // stripe.js has not loaded yet correctly
            return;
        }

        // create a payment method
        const { error, paymentMethod } = await stripe.createPaymentMethod({
            type: 'card',
            card: elements.getElement(CardElement),
        });

        // check for error
        if (error) {
            console.error(error);
            setLoading(false);
            // show error message
            setError(error?.message);
        }
        // handle successful payment method creation
        else {

            // add price and currency to payment method
            let paymentObject = {
                ...paymentMethod,
                amount: amount,
                currency: 'eur'
            };

            // set local state to indicate form submission
            setSubmitted(true);

            // send paymentMethod.id to backend for creating payment intent
            dispatch(getPaymentIntent(paymentObject))

        }
    };

    // handle stripe card payment confirmation
    const handleStripeCardPayment = async (paymentIntent) => {
        const cardElement = elements.getElement(CardElement);

        if (!cardElement) {
            console.error('CardElement is null. Make sure it is rendered before attempting payment.');
            setError('Card details are missing. Please re-enter your card information.');
            setLoading(false);
            return;
        }

        let clientSecret = paymentIntent.clientSecret;
        let paymentIntentId = paymentIntent.paymentIntentId;

        console.log('client secret', clientSecret);
        console.log('payment intent id', paymentIntentId);

        try {
            const { error, paymentIntent: confirmedPaymentIntent } = await stripe.confirmCardPayment(clientSecret, {
                payment_method: {
                    card: cardElement,
                },
            });

            if (error) {
                console.error('Payment confirmation error:', error);
                setError(error.message);
                setLoading(false);
            } else if (confirmedPaymentIntent?.status === 'succeeded') {
                console.log('payment complete...');
                handleSaveTransaction(paymentIntent);
            } else {
                setError('Payment was not completed.');
                setLoading(false);
            }
        } catch (error) {
            console.error('Error confirming payment:', error);
            setError('Failed to confirm payment. Please try again later.');
            setLoading(false);
        }
    };

    // handles saving the transaction to the backend after payment complete
    const handleSaveTransaction = (paymentIntent) => {

        console.log('saving transaction...');

        // set local state to indicate transaction is being saved
        setSavingTransaction(true);

        let paymentIntentId = paymentIntent.paymentIntentId;

        // handle product list using shopping cart
        const productList = Object.values(shoppingCart);

        // save transaction
        dispatch(saveTransaction({
            paymentIntentId: paymentIntentId,
            productList: productList,                   // use list of products from shopping cart
            amount: amount,                             // total amount of the transaction
            retailerId: retailerId,                     // selected retailer ID
            name: userName,                             // user name from form
            takeCode: pin,                              // randomised 4 digit pin to open robot
            tableNumber: tableNumber,                    // selected table number
            email: userEmail,                           // user email from form
        }));
    }

    //#endregion

    useEffect(() => {
        console.log('expressIntent', expressIntent)
    }, [expressIntent]);

    useEffect(() => {
        console.log('getPaymentExpressIntentStatus', getPaymentExpressIntentStatus)
    }, [getPaymentExpressIntentStatus])

    return (
        <div style={styles.container}>
            <div>

                {/* header with close button */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: Spacing.SCALE_12, }}>

                    <h5 style={{ margin: 0, fontWeight: 'bold' }}>Complete Order</h5>

                    <button onClick={onClose} style={styles.xButton}>
                        ×
                    </button>
                </div>

                {/* form */}
                <form onSubmit={handleSubmitCardPayment}>

                    <p>Please fill in the following to complete your order</p>

                    {/* name + email */}
                    <div>
                        <Form.Group className="mb-3" controlId="formName">
                            <Form.Label>Name*</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter your name"
                                value={userName}
                                onChange={(e) => dispatch(setUserName(e.target.value))}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3" controlId="formEmail">
                            <Form.Label>Email*</Form.Label>
                            <Form.Control
                                type="email"
                                placeholder="Enter your email"
                                value={userEmail}
                                onChange={(e) => dispatch(setUserEmail(e.target.value))}
                            />
                        </Form.Group>

                    </div>

                    {/* payment method selection */}
                    <Form.Label>Payment Method</Form.Label>

                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <button
                            type="button"
                            style={{ ...styles.expressPayButton, boxShadow: !disabled ? '0 2px 4px rgba(0,0,0,0.2)' : 'none' }}
                            onClick={(e) => {
                                e.preventDefault();
                                handleExpressPay();
                            }}
                            disabled={loading || disabled}
                        >
                            <img src={disabled ? ApplePayLogoGrey : ApplePayLogo} alt="Apple Pay" style={styles.logo} />
                            <img src={disabled ? GooglePayLogoGrey : GooglePayLogo} alt="Google Pay" style={styles.logo} />
                        </button>
                    </div>

                    {/* express pay option only if payment intent retreieved*/}
                    <div className='mt-3' style={{}}>
                        {getPaymentExpressIntentStatus == 'loading' ? <Spinner size={'small'} /> :
                            getPaymentExpressIntentStatus == 'succeeded' && expressIntent?.clientSecret != null && expressIntent?.clientSecret != undefined ? (
                                <div>

                                    {/* express checkout element */}
                                    <div style={{}}>
                                        <ExpressCheckoutElement
                                            options={{
                                                amount: amount * 100, currency: 'eur', mode: 'payment', clientSecret: paymentIntent?.clientSecret,
                                                paymentRequest: {

                                                    // country and currency
                                                    country: 'IE', currency: 'eur',

                                                    // total amount
                                                    total: {
                                                        label: 'Total',
                                                        amount: amount * 100
                                                    },
                                                    requestPayerName: true,
                                                    requestPayerEmail: true,
                                                },
                                                return_url: `${window.location.origin}/order-complete`,
                                            }}

                                            onConfirm={handleExpressConfirm}
                                            onReady={() => { setExpressPayReady(true) }}
                                            onCancel={() => console.log('Apple Pay cancelled')}
                                            onError={(e) => console.error('Apple Pay Error', e)}
                                        />
                                    </div>
                                </div>
                            )
                                :
                                null
                        }
                    </div>

                    <div style={styles.orDivider}>
                        <hr style={styles.hr} />
                        <span style={styles.orText}>OR</span>
                        <hr style={styles.hr} />
                    </div>


                    {/* card payment option + cancel / pay buttons*/}
                    <div className='mt-2'>
                        <Card>
                            <Card.Body>
                                <CardElement
                                    options={{
                                        hidePostalCode: true,
                                        style: {
                                            base: {
                                                fontSize: '16px', // Prevents iOS zoom
                                                color: '#32325d',
                                                '::placeholder': {
                                                    color: '#a0aec0',
                                                },
                                            },
                                        },
                                    }}

                                />
                            </Card.Body>
                        </Card>

                        {/* payment buttons */}
                        <div style={styles.buttonContainer}>

                            {/* Cancel Button */}
                            <button type="button" onClick={onClose} disabled={loading} style={{ opacity: loading ? 0.5 : 1, ...styles.closeButton }}>Cancel</button>

                            {/* Pay Button */}
                            <button
                                type="submit"
                                disabled={loading || disabled}
                                style={{ opacity: disabled ? 0.2 : 1, ...styles.payButton }}>
                                {loading ? <CustomSpinner size={'small'} variant={'white'} /> : 'Pay'}
                            </button>
                        </div>

                    </div>

                </form>

                {/* error message */}
                {error && (
                    <div style={{ maxWidth: 800, margin: '20px auto' }}>
                        <Alert variant="danger" style={styles.alert}>
                            {error}
                        </Alert>
                    </div>
                )}

            </div>
        </div>
    );
};

const styles = {
    container: {
        maxHeight: '90vh',
        overflowY: 'auto',
        padding: '20px',
    },
    xButton: {
        background: 'none',
        border: 'none',
        fontSize: 20,
        fontWeight: 'bold',
        cursor: 'pointer',
        color: '#888',
    },
    closeButton: {
        padding: `${Spacing.SCALE_6}px ${Spacing.SCALE_16}px`,
        fontSize: 14,
        backgroundColor: Colors.DARK_RED,
        color: '#fff',
        border: 'none',
        borderRadius: Spacing.SCALE_6,
        cursor: 'pointer',
        fontWeight: 'bold',
        minWidth: Spacing.SCALE_100,
        textAlign: 'center',
    },
    payButton: {
        padding: `${Spacing.SCALE_6}px ${Spacing.SCALE_16}px`,
        fontSize: 14,
        backgroundColor: Colors.PRM_LIGHT_BLUE,
        color: '#fff',
        border: 'none',
        borderRadius: Spacing.SCALE_6,
        cursor: 'pointer',
        fontWeight: 'bold',
        minWidth: Spacing.SCALE_100,
        textAlign: 'center',
    },
    buttonContainer: {
        marginTop: Spacing.SCALE_16,
        display: 'flex',
        justifyContent: 'center',
        gap: Spacing.SCALE_12
    },
    alert: {
        color: '#842029',
        border: '1px solid #f5c2c7',
        borderRadius: '8px',
        padding: '12px 18px',
        fontWeight: 500
    },
    expressPayButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#e9e9e9',
        border: 'none',
        borderRadius: 12,
        //padding: '10px 16px',
        gap: 8,
        cursor: 'pointer',
        //marginBottom: Spacing.SCALE_16,
        width: '100%',
    },
    logo: {
        width: 50,
        height: 50,
    },
    orDivider: {
        display: 'flex',
        alignItems: 'center',
        textAlign: 'center',
        margin: '24px 0',
        color: '#777',
        fontWeight: '500',
    },
    hr: {
        flex: 1,
        border: 'none',
        borderTop: '1px solid #ccc',
        margin: '0 12px',
    },
    orText: {
        whiteSpace: 'nowrap',
    },
};

export default CheckoutForm;

